import { useState, useEffect } from 'react';
import ProductsList from '../../components/products/ProductsList';
import AboutPopup from '../../components/common/AboutPopup';
import { CategoryDocuments } from '../../components/shared/CategoryDocuments';
import { PageWrapper, PageContainer, PageHeader } from '../../components/common/PageWrapper.styles';
import { FrontPageBtnList, FrontPageLinkUnit, FrontPageLinkUnitAnchor, FrontPageContent } from './HomePage.styles';

type ContentType =
    | ''
    | 'conversion-calculator'
    | 'ibc-code'
    | 'uscg'
    | 'fosfa-niop-eu'
    | 'rgm'
    | 'external-links'
    | 'cargo-related'
    | 'cleaning-chemicals'
    | 'training-materials';

const HomePage = () => {
    const [activeContent, setActiveContent] = useState<ContentType>('');
    const [searchTerm] = useState<string>(''); // For now, we'll handle search within ProductsList
    const [showAboutPopup, setShowAboutPopup] = useState<boolean>(false);
    const [showPreviouslyVisited, setShowPreviouslyVisited] = useState<boolean>(false);

    useEffect(() => {
        // Show previously visited products when no content is selected and no search is active
        setShowPreviouslyVisited(activeContent === '' && !searchTerm);
    }, [activeContent, searchTerm]);

    const setContent = (content: ContentType) => {
        setActiveContent(content);
    };

    return (
        <PageWrapper>
            {/* Show main content when no search is active */}
            <div className={searchTerm ? 'hidden' : ''}>
                <PageContainer>
                    {/* Header Section */}
                    <PageHeader>
                        <div id="logo-container">
                            <img src="/assets/images/odfjell_logo.jpg" alt="Odfjell Logo" />
                        </div>
                        <div className="front-heading">
                            <h3>Welcome to the Odfjell Commodity Book</h3>
                            <p>
                                <a href="#/disclaimer/">Disclaimer and terms of use.</a>
                            </p>
                            <p>
                                <span title="Download User Guide">
                                    <span className="glyphicon-circle-question-mark" />
                                    <a href="/assets/images/help.pdf" target="_blank" rel="noopener noreferrer">
                                        User Guide
                                    </a>
                                </span>
                                {' | '}
                                <button className="about-button" onClick={() => setShowAboutPopup(true)} title="User Profile and System Information">
                                    About
                                </button>
                            </p>
                        </div>
                    </PageHeader>

                    {/* Navigation Button Grid */}
                    <FrontPageBtnList>
                        <FrontPageLinkUnitAnchor href="/conversion-calculator" id="front-page-conversion-tools">
                            <div></div>
                            <h4>Conversion Tools</h4>
                        </FrontPageLinkUnitAnchor>
                        <FrontPageLinkUnit
                            id="front-page-ibc-code"
                            className={activeContent === 'ibc-code' ? 'active' : ''}
                            onClick={() => setContent('ibc-code')}
                        >
                            <div></div>
                            <h4>IBC Code</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit
                            id="front-page-uscg"
                            className={activeContent === 'uscg' ? 'active' : ''}
                            onClick={() => setContent('uscg')}
                        >
                            <div></div>
                            <h4>USCG</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit
                            id="front-page-fosfa-niop-eu"
                            className={activeContent === 'fosfa-niop-eu' ? 'active' : ''}
                            onClick={() => setContent('fosfa-niop-eu')}
                        >
                            <div></div>
                            <h4>FOSFA, NIOP, EU</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit id="front-page-rgm" className={activeContent === 'rgm' ? 'active' : ''} onClick={() => setContent('rgm')}>
                            <div></div>
                            <h4>RGM</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit
                            id="front-page-external-links"
                            className={activeContent === 'external-links' ? 'active' : ''}
                            onClick={() => setContent('external-links')}
                        >
                            <div></div>
                            <h4>EXTERNAL LINKS</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit
                            id="front-page-cargo-related"
                            className={activeContent === 'cargo-related' ? 'active' : ''}
                            onClick={() => setContent('cargo-related')}
                        >
                            <div></div>
                            <h4>CARGO RELATED</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit
                            id="front-page-cleaning-chemicals"
                            className={activeContent === 'cleaning-chemicals' ? 'active' : ''}
                            onClick={() => setContent('cleaning-chemicals')}
                        >
                            <div></div>
                            <h4>CLEANING CHEMICALS</h4>
                        </FrontPageLinkUnit>
                        <FrontPageLinkUnit
                            id="front-page-training-materials"
                            className={activeContent === 'training-materials' ? 'active' : ''}
                            onClick={() => setContent('training-materials')}
                        >
                            <div></div>
                            <h4>TRAINING MATERIALS</h4>
                        </FrontPageLinkUnit>
                    </FrontPageBtnList>
                </PageContainer>

                {/* Content Sections */}
                {activeContent !== '' && (
                    <PageContainer className="responsive-mobile">
                        <FrontPageContent id="front-page-content">
                            {activeContent === 'ibc-code' && (
                                <div>
                                    <h3>
                                        International Code for the Construction and Equipment of Ships Carrying Dangerous Chemicals in Bulk (IBC Code)
                                    </h3>
                                    <CategoryDocuments category="IBC" />
                                </div>
                            )}
                            {activeContent === 'uscg' && (
                                <div>
                                    <h3>United States Coast Guard</h3>
                                    <CategoryDocuments category="USCG" />
                                </div>
                            )}
                            {activeContent === 'fosfa-niop-eu' && (
                                <div>
                                    <h3>FOSFA, NIOP, EU</h3>
                                    <CategoryDocuments category="FOSFA" />
                                </div>
                            )}
                            {activeContent === 'rgm' && (
                                <div>
                                    <h3>Requirements and Guidelines Manual</h3>
                                    <CategoryDocuments category="RGM" />
                                </div>
                            )}
                            {activeContent === 'external-links' && (
                                <div>
                                    <h3>External Links</h3>
                                    <p>Useful external resources and regulatory websites.</p>
                                </div>
                            )}
                            {activeContent === 'cargo-related' && (
                                <div>
                                    <h3>Cargo Related</h3>
                                    <CategoryDocuments category="CargoRelated" />
                                </div>
                            )}
                            {activeContent === 'cleaning-chemicals' && (
                                <div>
                                    <h3>Cleaning Chemicals</h3>
                                    <CategoryDocuments category="CleaningChemicals" />
                                </div>
                            )}
                            {activeContent === 'training-materials' && (
                                <div>
                                    <h3>Training Materials</h3>
                                    <CategoryDocuments category="TrainingMaterials" />
                                </div>
                            )}
                        </FrontPageContent>
                    </PageContainer>
                )}
            </div>

            {/* Show ProductsList when no content is selected OR when search is active */}
            {(activeContent === '' || searchTerm) && (
                <PageContainer>
                    <ProductsList showPreviouslyVisited={showPreviouslyVisited} />
                </PageContainer>
            )}

            {/* About Popup */}
            {showAboutPopup && <AboutPopup isOpen={showAboutPopup} onClose={() => setShowAboutPopup(false)} />}
        </PageWrapper>
    );
};

export default HomePage;
